/**
 * Contact-related utility functions
 */

import * as Api from '@ViewModels';
import { getDisplayName } from '../NameUtils';

/**
 * Gets contact company line
 * @source Original location: UiUtils.ts.backup line 140
 */
export const getContactCompanyLine = (contact: Api.IContact) => {
	return contact.jobTitle
		? `${contact.jobTitle}${contact.companyName ? ` at ${contact.companyName}` : ''}`
		: contact.companyName;
};

/**
 * Gets display name preferring first name over email
 * @source Original location: UiUtils.ts.backup line 146
 */
export const getDisplayNamePreferFirstNameOverEmail = (principal: Api.IPrincipal, short = false) => {
	if (principal?.firstName) {
		return `${principal.firstName} ${
			short && principal.lastName ? `${principal.lastName?.charAt(0)}.` : (principal.lastName ?? '')
		}`;
	}

	if (principal?.primaryEmail?.value) {
		return principal.primaryEmail.value;
	}

	return '';
};

/**
 * Gets display name with email first
 * @source Original location: UiUtils.ts.backup line 160
 */
export const getDisplayNameWithEmailFirst = (principal: Api.IPrincipal, short = false) => {
	if (principal?.primaryEmail?.value) {
		return principal.primaryEmail.value;
	}

	return getDisplayName(principal, short);
};

/**
 * Gets display name for bulk email
 * @source Original location: UiUtils.ts.backup line 168
 */
export const getDisplayNameForBulkEmail = (principal: Api.IPrincipal, short = false) => {
	if (!!principal?.firstName && !principal?.lastName && !!principal?.primaryEmail?.value) {
		return `${principal.firstName} (${principal.primaryEmail.value})`;
	}

	return getDisplayName(principal, short);
};

/**
 * Gets display name first name only
 * @source Original location: UiUtils.ts.backup line 176
 */
export const getDisplayNameFirstNameOnly = (principal: Api.IPrincipal) => {
	if (principal?.firstName) {
		return principal.firstName;
	}

	if (principal?.primaryEmail) {
		return principal.primaryEmail.value;
	}

	return null;
};

/**
 * Gets contact title and company
 * @source Original location: UiUtils.ts.backup line 873
 */
export const getContactTitleAndCompany = (contact: Api.IContact | Api.ContactViewModel) => {
	if (contact) {
		if (!!contact.jobTitle && !!contact.companyName) {
			return `${contact.jobTitle} at ${contact.companyName}`;
		}

		if (!!contact.jobTitle && !contact.companyName) {
			return contact.jobTitle;
		}

		if (!!contact.companyName && !contact.jobTitle) {
			return contact.companyName;
		}
	}
	return '';
};

/**
 * Gets contact filter criteria component count
 * @source Original location: UiUtils.ts.backup line 1088
 */
export const contactFilterCriteriaComponentCount = (criteria: Api.IContactFilterCriteria): number => {
	if (criteria) {
		if (!!criteria.criteria && criteria.criteria.length > 0) {
			return criteria.criteria.reduce((count, x) => (count += contactFilterCriteriaComponentCount(x)), 0);
		} else if (criteria.property) {
			return 1;
		}
	}

	return 0;
};

/**
 * Checks if contact filter criteria has value
 * @source Original location: UiUtils.ts.backup line 1070
 */
export const contactFilterCriteriaHasValue = (
	criteria: Api.IContactFilterCriteria,
	value: string,
	options?: { caseInsensitiveCompare?: boolean }
): boolean => {
	if (criteria) {
		const caseInsensitiveCompare = (!!options && !!options.caseInsensitiveCompare) || false;
		const mValue = caseInsensitiveCompare ? (value || '').toLocaleLowerCase() : value || '';
		if (!!criteria.criteria && criteria.criteria.length > 0) {
			return !!criteria.criteria.some(x => !!contactFilterCriteriaHasValue(x, value, options));
		}

		return (caseInsensitiveCompare ? (criteria.value || '').toLocaleLowerCase() : criteria.value || '') === mValue;
	}

	return false;
};

/**
 * Gets default owned by contact filter filter criteria
 * @source Original location: UiUtils.ts.backup line 2562
 */
export function getDefaultOwnedByContactFilterFilterCriteria(
	userSession: Api.UserSessionContext,
	sendEmailFromUserId?: string
): Api.IOwnershipFilter | null {
	return !userSession.account.preferences?.showAllContactsByDefault
		? {
				criteria: {
					property: Api.ContactFilterCriteriaProperty.OwnedBy,
					value: sendEmailFromUserId ?? userSession.user.id,
				},
			}
		: null;
}
